import React, { useEffect, useRef } from 'react';
import App from './App';
import reportWebVitals from './reportWebVitals';
import {
    ConfigurationProvider,
    useConfiguration
} from './contexts/configuration/ConfigurationContext';
import {
    ReactPlugin,
    AppInsightsContext
} from '@microsoft/applicationinsights-react-js';
import {
    ApplicationInsights,
    SeverityLevel
} from '@microsoft/applicationinsights-web';

const AppWithMsal = () => {
    const { msalInstance, spaConfiguration } = useConfiguration();
    // Held in refs so the instance/plugin are created once instead of on every
    // render — recreating them would tear down and re-init telemetry each time.
    const reactPluginRef = useRef(new ReactPlugin());
    const appInsightsRef = useRef<ApplicationInsights | null>(null);
    const smokeTraceSentRef = useRef(false);

    if (!appInsightsRef.current) {
        appInsightsRef.current = new ApplicationInsights({
            config: {
                connectionString: spaConfiguration.appInsightsConnectionString,
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true,
                enableAutoRouteTracking: true,
                extensions: [reactPluginRef.current]
            }
        });
        appInsightsRef.current.loadAppInsights();
    }

    useEffect(() => {
        // One-shot telemetry smoke test: proves the App Insights pipeline is
        // live when the Backoffice boots. Filter in the portal on the custom
        // property { event: 'bo-load-smoke-test' }. Remove once verified.
        if (
            smokeTraceSentRef.current ||
            !spaConfiguration.appInsightsConnectionString
        ) {
            return;
        }
        smokeTraceSentRef.current = true;
        appInsightsRef.current?.trackTrace(
            {
                message: 'Backoffice loaded',
                severityLevel: SeverityLevel.Information
            },
            { event: 'bo-load-smoke-test' }
        );
        appInsightsRef.current?.flush();
    }, [spaConfiguration.appInsightsConnectionString]);

    return (
        <>
            {msalInstance ? (
                <AppInsightsContext.Provider value={reactPluginRef.current}>
                    <App pca={msalInstance} />
                </AppInsightsContext.Provider>
            ) : (
                <></>
            )}
        </>
    );
};

const AppRoot = () => {
    return (
        <ConfigurationProvider>
            <AppWithMsal />
        </ConfigurationProvider>
    );
};

export default AppRoot;

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();
